import { useEffect, useState } from 'react';
import { onKeyboardHeight } from './nativeKeyboard.ts';

/**
 * How tall the on-screen keyboard is, so the smiley panel can take exactly its
 * place.
 *
 * The handoff's requirement is that the thread must not jump when one replaces
 * the other, which means the panel has to know a height the web never tells it:
 * there is no keyboard API, and by the time the panel opens the keyboard is
 * already on its way out. So it is measured while the keyboard is up — the gap
 * between the layout viewport and the visual viewport is the keyboard, near
 * enough — and remembered for next time.
 *
 * Inside the shell there is a keyboard API after all, and it is preferred:
 * `keyboardWillShow` carries the height the OS is animating to. Not merely
 * because it is exact — the measurement stops working there. With the web view
 * resized around the keyboard (see lib/nativeKeyboard.ts) the two viewports
 * agree again, and a gap of nothing is what is left to measure.
 *
 * Remembered per device rather than per session because the very first tap on
 * the smiley button would otherwise be the one time it guesses wrong, and that
 * is the tap someone judges the feature by.
 */
const KEY = 'keyboardInset.v1';

/** The handoff's own figure, used until this device has shown us a real one. */
export const DEFAULT_KEYBOARD_INSET = 300;

/**
 * Below this, the gap is a toolbar, an address bar collapsing, or a rounding
 * difference — not a keyboard. Above it, no keyboard is that tall and something
 * else is going on.
 */
const MIN_INSET = 160;
const MAX_INSET = 520;

function remembered(): number {
  try {
    const raw = Number(localStorage.getItem(KEY));
    return raw >= MIN_INSET && raw <= MAX_INSET ? raw : DEFAULT_KEYBOARD_INSET;
  } catch {
    return DEFAULT_KEYBOARD_INSET;
  }
}

function remember(inset: number): void {
  try {
    localStorage.setItem(KEY, String(inset));
  } catch {
    // A height is not worth interrupting anyone over.
  }
}

export function useKeyboardInset(): number {
  const [inset, setInset] = useState(remembered);

  useEffect(() => {
    const accept = (height: number) => {
      if (height < MIN_INSET || height > MAX_INSET) return;
      setInset((current) => {
        if (Math.abs(current - height) < 2) return current;
        remember(height);
        return height;
      });
    };

    // Both sources run wherever they work; the clamp settles it. On the phone
    // the measurement now finds nothing — the viewports agree once the web view
    // has been resized — and a nothing is below MIN_INSET, so it is discarded
    // rather than believed.
    const stopListening = onKeyboardHeight((height) => {
      if (height !== null) accept(Math.round(height));
    });

    const viewport = window.visualViewport;
    if (!viewport) return stopListening;

    const measure = () => {
      // offsetTop covers the case where the page is scrolled within the visual
      // viewport rather than resized, which is what iOS does without the shell.
      accept(Math.round(window.innerHeight - viewport.height - viewport.offsetTop));
    };

    measure();
    viewport.addEventListener('resize', measure);
    viewport.addEventListener('scroll', measure);
    return () => {
      stopListening();
      viewport.removeEventListener('resize', measure);
      viewport.removeEventListener('scroll', measure);
    };
  }, []);

  return inset;
}
